import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { combineLatest } from 'rxjs';
import {
  BlogpostDetail,
  BlogpostInput,
  SiteSummary,
  createDraftBlogpost,
  slugify,
} from '../../models/cms-models';
import { TagSelectorComponent } from '../../components/tag-selector/tag-selector';
import { BlogpostsService } from '../../services/blogposts-service';
import { SiteContextService } from '../../services/site-context.service';
import { TagsService } from '../../services/tags-service';

@Component({
  selector: 'app-blogpost-editor-page',
  imports: [CommonModule, FormsModule, RouterLink, TagSelectorComponent],
  templateUrl: './blogpost-editor-page.html',
  styleUrl: './blogpost-editor-page.css',
})
export class BlogpostEditorPage implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private blogpostsService = inject(BlogpostsService);
  private siteContext = inject(SiteContextService);
  private tagsService = inject(TagsService);
  private destroyRef = inject(DestroyRef);

  sites: SiteSummary[] = [];
  blogpost: BlogpostDetail | null = null;
  siteId: string | null = null;
  loading = false;
  saving = false;
  error: string | null = null;
  isCreateMode = false;
  tagSuggestions: string[] = [];
  private activeRouteKey: string | null = null;

  async ngOnInit(): Promise<void> {
    await this.siteContext.ensureLoaded();
    combineLatest([this.route.paramMap, this.siteContext.sites$, this.siteContext.activeSiteId$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([params, sites, activeSiteId]) => {
        void this.loadRouteState(params.get('blogpostId'), sites, activeSiteId);
      });
  }

  get pageTitle(): string {
    return this.blogpost?.title.trim() || (this.isCreateMode ? 'New blogpost' : 'Blogpost');
  }

  get siteName(): string | null {
    return this.sites.find((site) => site.id === this.siteId)?.name ?? null;
  }

  get canSubmitBlogpost(): boolean {
    return !!this.blogpost?.title.trim()
      && !!this.blogpost?.slug.trim()
      && !!this.blogpost?.metadata.summary.trim()
      && !!this.blogpost?.content.trim();
  }

  updateTitle(value: string): void {
    if (!this.blogpost) {
      return;
    }

    this.blogpost.title = value;
    if (this.isCreateMode && !this.blogpost.slug.trim()) {
      this.blogpost.slug = slugify(value);
    }
  }

  async saveBlogpost(): Promise<void> {
    if (!this.blogpost || !this.canSubmitBlogpost) {
      return;
    }

    this.saving = true;
    this.error = null;

    try {
      if (this.isCreateMode) {
        const created = await this.blogpostsService.createBlogpost(this.toInput(this.blogpost));
        this.blogpost = created;
        this.siteId = created.siteId;
        this.siteContext.setActiveSite(created.siteId);
      } else {
        const updated = await this.blogpostsService.updateBlogpost(this.blogpost.id, this.toInput(this.blogpost));
        this.blogpost = updated;
        this.siteId = updated.siteId;
        this.siteContext.setActiveSite(updated.siteId);
      }

      await this.router.navigate(['/blogposts']);
    } catch {
      this.error = this.isCreateMode ? 'Failed to create the blogpost.' : 'Failed to save the blogpost.';
    } finally {
      this.saving = false;
    }
  }

  async deleteBlogpost(): Promise<void> {
    if (!this.blogpost || !this.siteId) {
      return;
    }

    const confirmed = window.confirm('Delete this blogpost? This cannot be undone.');
    if (!confirmed) {
      return;
    }

    this.saving = true;
    this.error = null;

    try {
      await this.blogpostsService.deleteBlogpost(this.blogpost.id);
      this.siteContext.setActiveSite(this.siteId);
      void this.router.navigate(['/blogposts']);
    } catch {
      this.error = 'Failed to delete the blogpost.';
    } finally {
      this.saving = false;
    }
  }

  private async loadRouteState(blogpostId: string | null, sites: SiteSummary[], activeSiteId: string | null): Promise<void> {
    this.sites = sites;

    if (!sites.length) {
      this.loading = false;
      this.blogpost = null;
      this.siteId = null;
      this.tagSuggestions = [];
      return;
    }

    const resolvedSiteId = this.siteContext.ensureValidActiveSite(activeSiteId);
    if (!resolvedSiteId) {
      return;
    }

    const routeKey = blogpostId ? `blogpost:${blogpostId}` : `new:${resolvedSiteId}`;
    if (this.activeRouteKey === routeKey) {
      return;
    }

    this.activeRouteKey = routeKey;
    this.loading = true;
    this.error = null;

    try {
      if (blogpostId) {
        this.isCreateMode = false;
        const blogpost = await this.blogpostsService.getBlogpost(blogpostId);
        this.blogpost = blogpost;
        this.siteId = blogpost.siteId;
        this.tagSuggestions = await this.tagsService.getSiteTags(blogpost.siteId);
        this.siteContext.setActiveSite(blogpost.siteId);
      } else {
        this.isCreateMode = true;
        this.siteId = resolvedSiteId;
        this.blogpost = createDraftBlogpost(resolvedSiteId);
        this.tagSuggestions = await this.tagsService.getSiteTags(resolvedSiteId);
        this.siteContext.setActiveSite(resolvedSiteId);
      }
    } catch {
      this.activeRouteKey = null;
      this.error = blogpostId ? 'Failed to load the blogpost.' : 'Failed to prepare a new blogpost.';
      this.blogpost = null;
      this.tagSuggestions = [];
    } finally {
      this.loading = false;
    }
  }

  private toInput(blogpost: BlogpostDetail): BlogpostInput {
    return {
      siteId: blogpost.siteId,
      title: blogpost.title.trim(),
      metadata: {
        summary: blogpost.metadata.summary.trim(),
        metaTitle: blogpost.metadata.metaTitle?.trim() || null,
        metaDescription: blogpost.metadata.metaDescription?.trim() || null,
        metaImageUrl: blogpost.metadata.metaImageUrl?.trim() || null,
      },
      content: blogpost.content.trim(),
      slug: slugify(blogpost.slug),
      publish: blogpost.published,
      tags: [...blogpost.tags],
    };
  }
}
